import { t } from '../../services/locale';
import type {
  ProfileAccountBranch,
  ProfileAccountFormatters,
  ProfileAccountUser,
  ProfileBranchDocument,
  ProfileDocumentRow,
  ProfileDocumentType,
  ProfileUserDocument,
} from './profileAccount.types';

export const DEFAULT_PROFILE_DATE_FORMAT = 'DD-MM-YYYY';

export function getProfileBranchDateFormat(branch: ProfileAccountBranch | null | undefined): string {
  return branch?.configuration?.formats?.date || DEFAULT_PROFILE_DATE_FORMAT;
}

/**
 * Port of `ProfileController.nameResolver`. The template pipes the value
 * through `translate`, so the label is resolved here for both types.
 */
export function resolveDocumentName(type: ProfileDocumentType | null | undefined): string {
  if (type === 'member.authenticated') {
    return t('WEB_STUDIO_WAIVER');
  }
  if (type === 'membership.purchased') {
    return t('WEB_MEMBERSHIP_TERMS_AND_CONDITIONS');
  }
  return '';
}

/** Port of `ProfileController.statusResolver`: accepted (even if outdated) and not a bare confirmation. */
export function resolveDocumentSigned(
  status: ProfileUserDocument['status'],
  kind: ProfileUserDocument['kind']
): boolean {
  return (status === 'accepted' || status === 'accepted_outdated') && kind !== 'confirmation';
}

/** Port of `ProfileController.dateResolver`. */
export function resolveDocumentSignedDate(
  agreedAt: string | null | undefined,
  branch: ProfileAccountBranch | null | undefined,
  formatLocalDate: ProfileAccountFormatters['formatLocalDate']
): string {
  if (!agreedAt) {
    return '';
  }
  return formatLocalDate(agreedAt, getProfileBranchDateFormat(branch));
}

/**
 * Port of the `userMembershipId` branch in `documentUrlResolver`: only
 * membership T&C documents are scoped to the user's membership.
 */
export function getDocumentUserMembershipId(
  type: ProfileDocumentType | null | undefined,
  user: ProfileAccountUser | null | undefined
): string | false {
  if (type !== 'membership.purchased') {
    return false;
  }
  return user?.membership?.user_membership_id || false;
}

export type ResolveDocumentsContext = {
  branch: ProfileAccountBranch | null | undefined;
  user: ProfileAccountUser | null | undefined;
  formatLocalDate: ProfileAccountFormatters['formatLocalDate'];
  /** `UserService.getDocumentUrl(branchId, userId, documentId, userMembershipId)` minus the ids the host owns. */
  resolveDocumentUrl: (documentId: string, userMembershipId: string | false) => string;
};

/**
 * Port of `ProfileController.resolveDocuments`: joins the user's agreements
 * with the branch documents on `document_id === _id`. Returns `[]` until both
 * lists are loaded; the controller leaves `vm.documents` untouched in that case.
 */
export function resolveDocuments(
  userDocuments: ProfileUserDocument[] | null | undefined,
  branchDocuments: ProfileBranchDocument[] | null | undefined,
  context: ResolveDocumentsContext
): ProfileDocumentRow[] {
  if (!userDocuments?.length || !branchDocuments?.length) {
    return [];
  }
  return userDocuments.map((userDocument) => {
    const document = branchDocuments.find((branchDocument) => branchDocument._id === userDocument.document_id);
    return {
      name: document ? resolveDocumentName(document.type) : '',
      signed: resolveDocumentSigned(userDocument.status, userDocument.kind),
      signedDate: resolveDocumentSignedDate(userDocument.agreed_at, context.branch, context.formatLocalDate),
      documentUrl: context.resolveDocumentUrl(
        document?._id ?? '',
        getDocumentUserMembershipId(document?.type, context.user)
      ),
    };
  });
}
